/**
 * Project Workspace View (briefing §3)
 *
 * Tab layout for a project: Bilder (gallery + multi-select + Bild-Popup),
 * Grundriss (status card, tool itself stays unchanged) and Export
 * (overlay choice + ZIP download).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Download,
  Trash2,
  Loader2,
  Plus,
  Check,
  Sofa,
  LayoutGrid,
  Box,
  Link2,
  Pencil,
  AlertTriangle,
  PlayCircle,
  Image as ImageIcon,
  Calendar,
  Type,
  Maximize2,
  Palette,
  Upload,
  Eraser,
} from 'lucide-react';
import { saveAs } from 'file-saver';
import { motion, useReducedMotion } from 'framer-motion';
import { GalleryImage, DbImage, RoomType, BrandingAsset, AspectRatio, WatermarkPosition, LogoPosition } from '@/types';
import {
  fetchProjectGalleryImages,
  fetchProjectImages,
  deleteMultipleImages,
  getPublicUrl,
  updateProject,
} from '@/services/projectService';
import { fetchFloorplanImage } from '@/services/floorplanService';
import { fetchBrandingAssets, uploadOverlayAsset } from '@/services/brandingService';
import { fetchUserProfile, updateDefaultBrandingDefaults } from '@/services/paymentService';
import { applyBrandingDetailed, compressForExport, renderExportPreview, type BrandingOptions } from '@/services/imageProcessing';
import { applyAiLabel, buildExportZip, exportZipFileName, sanitizeExportName, type ExportEntry, type FloorplanEntry } from '@/services/exportService';
import { AiLabelOption, useAiLabelPrefs } from '@/components/ui/AiLabelOption';
import { getRoomLabel } from '@/lib/roomConfig';
import {
  hasWizardSession,
  clearWizardSession,
  saveWizardSession,
  WizardStyleId,
  WizardAspectRatio,
} from '@/lib/wizardSession';
import { loadFloorplanSession, FLOORPLAN_STEP_LABEL } from '@/lib/floorplanSession';
import { overlaysForSelection, resolveOverlayUrlForAspect, overlayNeedsAspectWarning, ASPECT_VALUE } from '@/lib/overlayVariants';
import { useAuth } from '@/context/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Lightbox } from '@/components/ui/Lightbox';
import { cn } from '@/lib/utils';
import { ImageInspectorModal } from '@/components/ui/ImageInspectorModal';
import type { InspectorRoom } from '@/components/ui/ImageInspectorModal';

// ============================================================================
// Contract (frozen)
// ============================================================================

export interface ProjectWorkspaceViewProps {
  project: import('@/types').Project;
  isAdmin: boolean;
  onCreditsRefresh: () => void;
  onAddImages: () => void; // App opens the wizard in 'add' mode
  onOpenFloorplan: () => void; // App navigates to the existing floorplan flow
  onOpenFloorplanBranding: () => void; // App navigates directly to the floorplan Branding-Studio
  onBack: () => void; // to dashboard
  onProjectUpdated: (updates: Partial<import('@/types').Project>) => void; // sync App state after rename
}

// ============================================================================
// Internal types
// ============================================================================

type WorkspaceTab = 'bilder' | 'grundriss' | 'export';
type GalleryFilter = 'alle' | 'ein' | 'leer';
type PreviewKind = 'original' | 'empty' | 'staged' | 'branded';
type ExportQuality = 'full' | 'web';
type OverlayChoice = 'none' | 'own';

interface ExportTile {
  key: string;
  label: string;
  url: string;
  group: 'leer' | 'ein' | 'plan';
  imageId?: string;
  roomLabel?: string;
  variant?: 'leer' | 'eingerichtet';
  side?: '2d' | '3d';
  branded?: boolean;
}

const WATERMARK_POSITIONS: { position: WatermarkPosition; label: string }[] = [
  { position: 'center', label: 'Mitte' },
  { position: 'tile', label: 'Gekachelt' },
  { position: 'tl', label: 'Oben links' },
  { position: 'tr', label: 'Oben rechts' },
  { position: 'bl', label: 'Unten links' },
  { position: 'br', label: 'Unten rechts' },
];

const LOGO_POSITIONS: { position: LogoPosition; label: string }[] = [
  { position: 'top-left', label: 'Oben links' },
  { position: 'top-right', label: 'Oben rechts' },
  { position: 'bottom-left', label: 'Unten links' },
  { position: 'bottom-right', label: 'Unten rechts' },
];

const preferredPreview = (img: GalleryImage): PreviewKind => {
  if (img.stagedUrl) return 'staged';
  if (img.emptyUrl) return 'empty';
  return 'original';
};

const previewUrl = (img: GalleryImage, kind: PreviewKind): string | undefined => {
  switch (kind) {
    case 'branded':
      return img.brandedUrl ?? img.brandedEmptyUrl ?? img.stagedUrl ?? img.emptyUrl ?? img.originalUrl;
    case 'staged':
      return img.stagedUrl ?? img.emptyUrl ?? img.originalUrl;
    case 'empty':
      return img.emptyUrl ?? img.originalUrl;
    default:
      return img.originalUrl;
  }
};

export const kindForFilter = (filter: GalleryFilter, img: GalleryImage): PreviewKind => {
  if (filter === 'ein') return img.stagedUrl ? 'staged' : preferredPreview(img);
  if (filter === 'leer') return img.emptyUrl ? 'empty' : preferredPreview(img);
  return preferredPreview(img);
};

export const originalBeschriftungValue = (
  config: import('@/types').FloorplanConfig | null
): 'entfernt' | 'beibehalten' | null => {
  if (!config) return null;
  return config.textHandling === 'remove' ? 'entfernt' : 'beibehalten';
};

export const pickPlanPath = (cleanPath: string, brandedPath?: string | null): string =>
  brandedPath ?? cleanPath;

export const overlayPreviewUrl = (images: GalleryImage[]): string | null => {
  const staged = images.find((i) => i.stagedUrl)?.stagedUrl;
  if (staged) return staged;
  const empty = images.find((i) => i.emptyUrl)?.emptyUrl;
  return empty ?? null;
};

const imageDisplayName = (img: GalleryImage): string =>
  img.customLabel || getRoomLabel(img.roomType);

const statusLine = (img: GalleryImage): string => {
  const parts: string[] = [];
  if (img.emptyUrl) parts.push('Leer');
  if (img.stagedUrl) parts.push('Eingerichtet');
  if (img.brandedUrl || img.brandedEmptyUrl) parts.push('Gebrandet');
  return parts.length ? `${parts.join(' + ')} ✓` : 'Nur Original';
};

const sanitizeFileName = (name: string): string => name.replace(/\s+/g, '_').replace(/[/\\]/g, '-');

// ============================================================================
// Component
// ============================================================================

export const ProjectWorkspaceView: React.FC<ProjectWorkspaceViewProps> = ({
  project,
  isAdmin,
  onCreditsRefresh,
  onAddImages,
  onOpenFloorplan,
  onOpenFloorplanBranding,
  onBack,
  onProjectUpdated,
}) => {
  const { user } = useAuth();
  const prefersReducedMotion = useReducedMotion();

  // Tab deep-link: /app/workspace?p=<id>&tab=bilder|plan|grundriss|export
  const [tab, setTab] = useState<WorkspaceTab>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    if (t === 'bilder') return 'bilder';
    if (t === 'plan' || t === 'grundriss') return 'grundriss';
    if (t === 'export') return 'export';
    return 'bilder';
  });

  // Nicht abgeschlossener Assistent-Durchlauf für dieses Projekt? (beim Mount gelesen —
  // der Workspace wird beim Navigieren neu gemountet, daher stets aktuell)
  const [hasResumableFlow, setHasResumableFlow] = useState(() => hasWizardSession(project.id));
  const [discardArmed, setDiscardArmed] = useState(false);
  // Nicht abgeschlossener Grundriss-Durchlauf jenseits des Upload-Schritts (beim Mount
  // gelesen — nur zeigen, wenn es echten Zwischenstand gibt, nicht schon bei "Hochladen").
  const [floorplanResume] = useState(() => {
    const s = loadFloorplanSession(project.id);
    return s && s.view !== 'floorplan-upload' ? s : null;
  });

  // Projekt umbenennen (Inline im Header)
  const [isRenaming, setIsRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(project.name);
  const [isSavingName, setIsSavingName] = useState(false);

  const startRename = () => {
    setNameDraft(project.name);
    setIsRenaming(true);
  };

  const submitRename = async () => {
    const next = nameDraft.trim();
    if (!next || next === project.name) {
      setIsRenaming(false);
      return;
    }
    setIsSavingName(true);
    try {
      await updateProject(project.id, { name: next });
      onProjectUpdated({ name: next });
      setIsRenaming(false);
    } catch (err) {
      console.error('[Workspace] Rename failed:', err);
      alert(err instanceof Error ? err.message : 'Umbenennen fehlgeschlagen');
    } finally {
      setIsSavingName(false);
    }
  };

  // Gallery data
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [rawImages, setRawImages] = useState<DbImage[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);
  const [imagesError, setImagesError] = useState<string | null>(null);

  // Floorplan
  const [floorplan, setFloorplan] = useState<DbImage | null>(null);
  const [isLoadingFloorplan, setIsLoadingFloorplan] = useState(true);
  const [floorplanLightbox, setFloorplanLightbox] = useState<{ url: string; name: string } | null>(null);

  // Gallery UI
  const [filter, setFilter] = useState<GalleryFilter>('alle');
  const [previewOverride, setPreviewOverride] = useState<Record<string, PreviewKind>>({});
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkZipping, setIsBulkZipping] = useState(false);

  // Inspector
  const [inspectorIndex, setInspectorIndex] = useState<number | null>(null);

  // Export
  const [overlays, setOverlays] = useState<BrandingAsset[]>([]);
  const [isLoadingOverlays, setIsLoadingOverlays] = useState(true);
  const [overlayChoice, setOverlayChoice] = useState<OverlayChoice>('none');
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null);
  const [exportQuality, setExportQuality] = useState<ExportQuality>('full');
  const [exportDeselected, setExportDeselected] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [overlayPreview, setOverlayPreview] = useState<string | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isUploadingOverlay, setIsUploadingOverlay] = useState(false);
  const overlayFileInputRef = useRef<HTMLInputElement>(null);

  // Wasserzeichen + Logo (Parität zum Wizard-Schritt 4)
  const [watermarkAssets, setWatermarkAssets] = useState<BrandingAsset[]>([]);
  const [logoAssets, setLogoAssets] = useState<BrandingAsset[]>([]);
  const [selectedWatermarkId, setSelectedWatermarkId] = useState<string | null>(null);
  const [selectedLogoId, setSelectedLogoId] = useState<string | null>(null);
  const [watermarkOpacity, setWatermarkOpacity] = useState(30); // %
  const [watermarkScale, setWatermarkScale] = useState(0.5); // 0.1–1
  const [watermarkPosition, setWatermarkPosition] = useState<WatermarkPosition>('center');
  const [logoPosition, setLogoPosition] = useState<LogoPosition>('bottom-right');
  const aiLabel = useAiLabelPrefs();

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadImages = useCallback(
    async (withSpinner: boolean) => {
      if (withSpinner) setIsLoadingImages(true);
      setImagesError(null);
      try {
        const [gallery, raw] = await Promise.all([
          fetchProjectGalleryImages(project.id),
          fetchProjectImages(project.id),
        ]);
        setGalleryImages(gallery);
        setRawImages(raw.filter((r) => r.room_type !== 'floorplan'));
      } catch (err) {
        console.error('[Workspace] Failed to load images:', err);
        setImagesError('Bilder konnten nicht geladen werden.');
      } finally {
        if (withSpinner) setIsLoadingImages(false);
      }
    },
    [project.id]
  );

  const loadFloorplan = useCallback(async () => {
    setIsLoadingFloorplan(true);
    try {
      const fp = await fetchFloorplanImage(project.id);
      setFloorplan(fp);
    } finally {
      setIsLoadingFloorplan(false);
    }
  }, [project.id]);

  useEffect(() => {
    void loadImages(true);
    void loadFloorplan();
  }, [loadImages, loadFloorplan]);

  useEffect(() => {
    const loadOverlays = async () => {
      if (!user) {
        setIsLoadingOverlays(false);
        return;
      }
      setIsLoadingOverlays(true);
      try {
        const [assets, wms, logos, profile] = await Promise.all([
          fetchBrandingAssets(user.id, 'overlay'),
          fetchBrandingAssets(user.id, 'watermark'),
          fetchBrandingAssets(user.id, 'logo'),
          fetchUserProfile(user.id).catch(() => null),
        ]);
        setOverlays(assets);
        setWatermarkAssets(wms);
        setLogoAssets(logos);
        if (assets.length > 0) {
          setOverlayChoice('own');
          setSelectedOverlayId((prev) => prev ?? assets[0].id);
        }
        // Standard-Wasserzeichen/-Logo aus dem Profil vorauswählen (wie im Wizard).
        if (profile) {
          if (profile.defaultWatermarkAssetId && wms.some((w) => w.id === profile.defaultWatermarkAssetId)) {
            setSelectedWatermarkId((prev) => prev ?? profile.defaultWatermarkAssetId);
            setWatermarkOpacity(profile.defaultWatermarkOpacity);
            setWatermarkScale(profile.defaultWatermarkScale);
            setWatermarkPosition(profile.defaultWatermarkPosition);
          }
          if (profile.defaultLogoAssetId && logos.some((l) => l.id === profile.defaultLogoAssetId)) {
            setSelectedLogoId((prev) => prev ?? profile.defaultLogoAssetId);
            setLogoPosition(profile.defaultLogoPosition);
          }
        }
      } catch (err) {
        console.error('[Workspace] Failed to load overlays:', err);
        setOverlays([]);
      } finally {
        setIsLoadingOverlays(false);
      }
    };
    void loadOverlays();
  }, [user]);

  // ── Derived gallery data ──────────────────────────────────────────────────

  const filteredImages = useMemo(() => {
    if (filter === 'ein') return galleryImages.filter((i) => !!i.stagedUrl);
    if (filter === 'leer') return galleryImages.filter((i) => !!i.emptyUrl);
    return galleryImages;
  }, [galleryImages, filter]);

  // Ausgewählte Bilder, die bereits eine Leer-Variante haben — nur für diese ist
  // "Einrichten" (Staging direkt auf dem leergeräumten Bild) sinnvoll.
  const selectedEmptyIds = useMemo(
    () => galleryImages.filter((i) => selectedIds.has(i.id) && !!i.emptyUrl).map((i) => i.id),
    [galleryImages, selectedIds]
  );

  const rawById = useMemo(() => {
    const map = new Map<string, DbImage>();
    rawImages.forEach((r) => map.set(r.id, r));
    return map;
  }, [rawImages]);

  const inspectorRooms = useMemo<InspectorRoom[]>(
    () =>
      filteredImages.map((img) => {
        const raw = rawById.get(img.id);
        return {
          imageId: img.id,
          name: imageDisplayName(img),
          roomType: (raw?.room_type ?? img.roomType) as RoomType,
          keepKitchen: raw?.keep_kitchen ?? false,
          originalUrl: img.originalUrl ?? null,
          originalPath: raw?.storage_path ?? null,
        };
      }),
    [filteredImages, rawById]
  );

  // Keep the inspector index valid when the filtered list changes
  useEffect(() => {
    if (inspectorIndex === null) return;
    if (inspectorRooms.length === 0) {
      setInspectorIndex(null);
    } else if (inspectorIndex >= inspectorRooms.length) {
      setInspectorIndex(inspectorRooms.length - 1);
    }
  }, [inspectorIndex, inspectorRooms.length]);

  const has2d = !!floorplan?.storage_path_floorplan_2d;
  const has3d = !!floorplan?.storage_path_floorplan_3d;
  const hasFloorplan = has2d || has3d;

  // Before/after tiles + metadata for the Grundriss tab.
  const floorplanTiles = useMemo(() => {
    if (!floorplan) return [] as { key: string; label: string; sub: string; url: string; name: string; accent: boolean }[];
    const tiles: { key: string; label: string; sub: string; url: string; name: string; accent: boolean }[] = [];
    const originalPath = floorplan.source_storage_path ?? floorplan.storage_path ?? null;
    if (originalPath) {
      tiles.push({ key: 'orig', label: 'Original', sub: 'hochgeladen', url: getPublicUrl(originalPath), name: 'Grundriss_Original', accent: false });
    }
    if (floorplan.storage_path_floorplan_2d) {
      tiles.push({ key: '2d', label: '2D-Grundriss', sub: 'generiert', url: getPublicUrl(floorplan.storage_path_floorplan_2d), name: 'Grundriss_2D', accent: true });
    }
    if (floorplan.storage_path_floorplan_2d_branded) {
      tiles.push({ key: '2d-branded', label: '2D-Grundriss', sub: 'gebrandet', url: getPublicUrl(floorplan.storage_path_floorplan_2d_branded), name: 'Grundriss_2D_Branding', accent: true });
    }
    if (floorplan.storage_path_floorplan_3d) {
      tiles.push({ key: '3d', label: '3D-Grundriss', sub: 'generiert', url: getPublicUrl(floorplan.storage_path_floorplan_3d), name: 'Grundriss_3D', accent: true });
    }
    if (floorplan.storage_path_floorplan_3d_branded) {
      tiles.push({ key: '3d-branded', label: '3D-Grundriss', sub: 'gebrandet', url: getPublicUrl(floorplan.storage_path_floorplan_3d_branded), name: 'Grundriss_3D_Branding', accent: true });
    }
    return tiles;
  }, [floorplan]);

  const floorplanConfig = floorplan?.floorplan_config ?? null;
  const floorplanCreatedLabel = floorplan?.created_at
    ? new Date(floorplan.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;
  const floorplanTextLabel = originalBeschriftungValue(floorplanConfig);

  const downloadFloorplanTile = useCallback(async (url: string, name: string) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      saveAs(blob, `${name}.png`);
    } catch {
      window.open(url, '_blank');
    }
  }, []);

  // ── Gallery actions ───────────────────────────────────────────────────────

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setConfirmBulkDelete(false);
  }, []);

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setConfirmBulkDelete(false);
  }, []);

  // Staging-Flow für bestehende Bilder starten: eine Session mit genau diesen
  // Bild-IDs anlegen und den Wizard öffnen — dessen Hydration rekonstruiert die
  // Räume und springt an den gewünschten Schritt.
  //   • startStep 2 (Einstellen) = "Leerräumen": Empty-first-Flow ab Konfig.
  //   • startStep 3 (Prüfen & Einrichten) = "Einrichten": die bereits leer-
  //     geräumten Varianten sind die Basis, direkt Staging pro Raum möglich.
  const startFlowForImages = useCallback(
    (imageIds: string[], startStep: 2 | 3) => {
      const ids = imageIds.filter(Boolean);
      if (ids.length === 0) return;
      saveWizardSession({
        projectId: project.id,
        step: startStep,
        style: (project.stagingStyle ?? 'modern') as WizardStyleId,
        aspect: (project.aspectRatio ?? '4:3') as WizardAspectRatio,
        overlayChoice: 'none',
        exportCompressed: false,
        roomImageIds: ids,
      });
      exitSelectMode();
      onAddImages();
    },
    [project, exitSelectMode, onAddImages]
  );

  const handleDiscardRun = useCallback(() => {
    if (!discardArmed) {
      setDiscardArmed(true);
      return;
    }
    clearWizardSession(project.id);
    setHasResumableFlow(false);
    setDiscardArmed(false);
  }, [discardArmed, project.id]);

  const handleQuickDownload = useCallback(async (img: GalleryImage, kind: PreviewKind) => {
    const url = previewUrl(img, kind);
    if (!url) return;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      saveAs(blob, `${sanitizeFileName(imageDisplayName(img))}.png`);
    } catch (err) {
      console.error('[Workspace] Quick download failed:', err);
    }
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    if (!confirmBulkDelete) {
      setConfirmBulkDelete(true);
      return;
    }
    setIsBulkDeleting(true);
    try {
      await deleteMultipleImages([...selectedIds]);
      exitSelectMode();
      await loadImages(false);
    } catch (err) {
      console.error('[Workspace] Bulk delete failed:', err);
      setImagesError('Bilder konnten nicht gelöscht werden.');
    } finally {
      setIsBulkDeleting(false);
      setConfirmBulkDelete(false);
    }
  }, [selectedIds, confirmBulkDelete, exitSelectMode, loadImages]);

  const handleBulkDownload = useCallback(async () => {
    const selected = filteredImages
      .filter((i) => selectedIds.has(i.id))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    if (selected.length === 0) return;
    setIsBulkZipping(true);
    setImagesError(null);
    try {
      const entries: ExportEntry[] = [];
      for (const img of selected) {
        const roomLabel = imageDisplayName(img);
        if (img.emptyUrl) entries.push({ imageId: img.id, roomLabel, variant: 'leer', source: img.emptyUrl });
        if (img.stagedUrl) entries.push({ imageId: img.id, roomLabel, variant: 'eingerichtet', source: img.stagedUrl });
        if (!img.emptyUrl && !img.stagedUrl && img.originalUrl) {
          entries.push({ imageId: img.id, roomLabel, variant: 'original', source: img.originalUrl });
        }
      }
      // KI-Hinweis zuletzt — der Fallback-Eintrag 'original' bleibt unberührt.
      const labeled = aiLabel.enabled
        ? await applyAiLabel(entries, [], aiLabel.position, aiLabel.opacity)
        : { entries, floorplans: [] };
      const result = await buildExportZip(labeled.entries, labeled.floorplans);
      saveAs(result.blob, `${sanitizeExportName(project.name) || 'estage'}_Auswahl.zip`);
      if (result.failed > 0) {
        setImagesError(`${result.added} von ${result.total} Dateien exportiert — einige Bilder konnten nicht geladen werden.`);
      }
    } catch (err) {
      console.error('[Export] Auswahl-ZIP fehlgeschlagen:', err);
      setImagesError('ZIP-Download fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setIsBulkZipping(false);
    }
  }, [filteredImages, selectedIds, project.name, aiLabel.enabled, aiLabel.position, aiLabel.opacity]);

  // ── Export tab ────────────────────────────────────────────────────────────

  const exportTiles = useMemo<ExportTile[]>(() => {
    const tiles: ExportTile[] = [];
    // Aufsteigend nach created_at: Kachel-Reihenfolge entspricht der Dateinummerierung (_01 = ältestes Bild).
    const sortedImages = [...galleryImages].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    sortedImages.forEach((img) => {
      const name = imageDisplayName(img);
      if (img.emptyUrl) {
        tiles.push({ key: `${img.id}:empty`, label: name, url: img.emptyUrl, group: 'leer', imageId: img.id, roomLabel: name, variant: 'leer' });
      }
      if (img.stagedUrl) {
        tiles.push({ key: `${img.id}:staged`, label: name, url: img.stagedUrl, group: 'ein', imageId: img.id, roomLabel: name, variant: 'eingerichtet' });
      }
    });
    if (floorplan?.storage_path_floorplan_2d) {
      tiles.push({
        key: 'plan:2d',
        label: '2D-Grundriss',
        url: getPublicUrl(pickPlanPath(floorplan.storage_path_floorplan_2d, floorplan.storage_path_floorplan_2d_branded)),
        group: 'plan',
        side: '2d',
        branded: !!floorplan.storage_path_floorplan_2d_branded,
      });
    }
    if (floorplan?.storage_path_floorplan_3d) {
      tiles.push({
        key: 'plan:3d',
        label: '3D-Ansicht',
        url: getPublicUrl(pickPlanPath(floorplan.storage_path_floorplan_3d, floorplan.storage_path_floorplan_3d_branded)),
        group: 'plan',
        side: '3d',
        branded: !!floorplan.storage_path_floorplan_3d_branded,
      });
    }
    return tiles;
  }, [galleryImages, floorplan]);

  const selectedExportTiles = useMemo(
    () => exportTiles.filter((t) => !exportDeselected.has(t.key)),
    [exportTiles, exportDeselected]
  );

  const toggleExportTile = useCallback((key: string) => {
    setExportDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Auswahl-Liste: pro Overlay nur eine Variante (4:3 bevorzugt).
  const displayOverlays = useMemo(() => overlaysForSelection(overlays), [overlays]);
  const activeOverlay: BrandingAsset | null =
    overlays.find((o) => o.id === selectedOverlayId) ?? displayOverlays[0] ?? null;
  const projectAspect: AspectRatio = project.aspectRatio ?? '4:3';

  const previewSample = useMemo(() => overlayPreviewUrl(galleryImages), [galleryImages]);
  const showExportPreview = !!previewSample;

  const selectedWatermark = watermarkAssets.find((w) => w.id === selectedWatermarkId) ?? null;
  const selectedLogo = logoAssets.find((l) => l.id === selectedLogoId) ?? null;

  // Vollständige Branding-Optionen für Export UND Vorschau (ein Code-Pfad).
  const exportBrandingOptions = useMemo<BrandingOptions>(
    () => ({
      overlayUrl:
        overlayChoice === 'own' && activeOverlay
          ? resolveOverlayUrlForAspect(activeOverlay, overlays, projectAspect)
          : null,
      watermarkUrl: selectedWatermark?.publicUrl ?? null,
      watermarkOpacity,
      watermarkScale,
      watermarkPosition,
      logoUrl: selectedLogo?.publicUrl ?? null,
      logoPosition,
    }),
    [overlayChoice, activeOverlay, overlays, projectAspect, selectedWatermark, watermarkOpacity, watermarkScale, watermarkPosition, selectedLogo, logoPosition]
  );
  const hasExportBranding = !!(exportBrandingOptions.overlayUrl || exportBrandingOptions.watermarkUrl || exportBrandingOptions.logoUrl);

  useEffect(() => {
    let cancelled = false;
    // Auch ohne Branding vorschauen, sobald der KI-Hinweis an ist — sonst zeigt
    // die Vorschau ein Bild ohne den Hinweis, den der Export einbrennt.
    if ((!hasExportBranding && !aiLabel.enabled) || !previewSample) {
      setOverlayPreview(null);
      setIsPreviewing(false);
      return;
    }
    setIsPreviewing(true);
    renderExportPreview(previewSample, exportBrandingOptions, aiLabel)
      .then((result) => {
        if (!cancelled) setOverlayPreview(result.url.startsWith('data:') ? result.url : null);
      })
      .catch(() => {
        if (!cancelled) setOverlayPreview(null);
      })
      .finally(() => {
        if (!cancelled) setIsPreviewing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasExportBranding, exportBrandingOptions, previewSample, aiLabel]);

  /** Neues Overlay direkt hochladen (PNG) — ersetzt den Weg über den Overlay-Builder. */
  const handleOverlayFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !user?.id) return;
      if (file.type !== 'image/png') {
        setExportError('Bitte eine PNG-Datei mit Transparenz hochladen.');
        return;
      }
      setIsUploadingOverlay(true);
      setExportError(null);
      try {
        const asset = await uploadOverlayAsset(user.id, file);
        setOverlays((prev) => [asset, ...prev]);
        setSelectedOverlayId(asset.id);
        setOverlayChoice('own');
      } catch (err) {
        console.error('[Workspace] Overlay upload failed:', err);
        setExportError('Upload fehlgeschlagen — bitte erneut versuchen.');
      } finally {
        setIsUploadingOverlay(false);
      }
    },
    [user?.id]
  );

  const openOverlayUpload = useCallback(() => overlayFileInputRef.current?.click(), []);

  const handleSelectOwnOverlay = useCallback(() => {
    if (!activeOverlay) {
      openOverlayUpload();
      return;
    }
    setOverlayChoice('own');
  }, [activeOverlay, openOverlayUpload]);

  const handleExportDownload = useCallback(async () => {
    if (selectedExportTiles.length === 0) return;
    // Warnen, wenn das Overlay keine Variante fürs Projekt-Format hat.
    if (
      overlayChoice === 'own' &&
      overlayNeedsAspectWarning(activeOverlay, overlays, ASPECT_VALUE[projectAspect]) &&
      !window.confirm(
        `Das gewählte Overlay hat keine ${projectAspect}-Variante. Es wird verzerrt auf die Bilder gelegt. Trotzdem exportieren?`
      )
    ) {
      return;
    }
    setIsExporting(true);
    setExportError(null);
    try {
      const ratio: AspectRatio = project.aspectRatio ?? '4:3';

      const entries: ExportEntry[] = [];
      const floorplans: FloorplanEntry[] = [];
      for (const tile of selectedExportTiles) {
        // Floorplans: always exported untouched (no overlay, no resize)
        if (tile.group === 'plan' && tile.side) {
          floorplans.push({ side: tile.side, source: tile.url });
          continue;
        }
        if (!tile.imageId || !tile.roomLabel || !tile.variant) continue;
        let source: string | Blob = tile.url;
        let ext: 'png' | 'jpg' = 'png';
        const needsCanvas = hasExportBranding || exportQuality === 'web';
        if (needsCanvas) {
          const branded = await applyBrandingDetailed(tile.url, exportBrandingOptions);
          if (branded.failedLayers.length > 0) {
            // Abbrechen statt still ohne Wasserzeichen/Logo/Overlay zu exportieren.
            setExportError(
              'Das Branding (z. B. Wasserzeichen) konnte nicht angewendet werden — Export abgebrochen. Tipp: dunkles Logo auf weißem oder transparentem Hintergrund verwenden, oder die Auswahl entfernen.'
            );
            return;
          }
          let dataUrl = branded.url;
          if (dataUrl.startsWith('data:')) {
            if (exportQuality === 'web') {
              dataUrl = await compressForExport(dataUrl, ratio);
              ext = 'jpg';
            }
            source = dataUrl; // data:-URLs sind fetchbar → buildExportZip lädt sie direkt
          }
          // applyBranding-Fallback (keine data:-URL): Original-URL bleibt als source
        }
        entries.push({ imageId: tile.imageId, roomLabel: tile.roomLabel, variant: tile.variant, source, ext });
      }

      // KI-Hinweis zuletzt — nach dem Branding, damit er nicht darunter liegt.
      const labeled = aiLabel.enabled
        ? await applyAiLabel(entries, floorplans, aiLabel.position, aiLabel.opacity)
        : { entries, floorplans };
      const result = await buildExportZip(labeled.entries, labeled.floorplans);
      saveAs(result.blob, exportZipFileName(project.name));
      if (result.failed > 0) {
        setExportError(`${result.added} von ${result.total} Dateien exportiert — einige Bilder konnten nicht geladen werden.`);
      }
      // Zuletzt genutztes Wasserzeichen/Logo als Standard merken.
      if (user?.id) {
        void updateDefaultBrandingDefaults(user.id, {
          watermarkAssetId: selectedWatermarkId,
          watermarkOpacity,
          watermarkScale: Math.round(watermarkScale * 100),
          watermarkPosition,
          logoAssetId: selectedLogoId,
          logoPosition,
        });
      }
    } catch (err) {
      console.error('[Export] Workspace-Export fehlgeschlagen:', err);
      setExportError('ZIP-Export fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setIsExporting(false);
    }
  }, [selectedExportTiles, project.name, project.aspectRatio, projectAspect, overlayChoice, activeOverlay, overlays, exportQuality, hasExportBranding, exportBrandingOptions, user?.id, selectedWatermarkId, watermarkOpacity, watermarkScale, watermarkPosition, selectedLogoId, logoPosition, aiLabel.enabled, aiLabel.position, aiLabel.opacity]);

  // ── Render helpers ────────────────────────────────────────────────────────

  const renderGalleryCard = (img: GalleryImage, idx: number) => {
    const kind = previewOverride[img.id] ?? kindForFilter(filter, img);
    const url = previewUrl(img, kind);
    const isSelected = selectedIds.has(img.id);
    const name = imageDisplayName(img);

    return (
      <Card
        key={img.id}
        className={cn(
          'overflow-hidden transition-shadow hover:shadow-md',
          selectMode && isSelected && 'ring-2 ring-primary'
        )}
      >
        <div
          className="relative aspect-[4/3] bg-slate-100 cursor-pointer group"
          onClick={() => {
            if (selectMode) toggleSelect(img.id);
            else setInspectorIndex(idx);
          }}
        >
          {url ? (
            <img src={url} alt={name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-10 h-10 text-slate-300" />
            </div>
          )}

          {/* Selection checkbox */}
          {selectMode && (
            <div
              className={cn(
                'absolute top-2 left-2 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors',
                isSelected ? 'bg-primary border-primary' : 'bg-white/80 border-slate-300'
              )}
            >
              {isSelected && <Check className="w-4 h-4 text-white" />}
            </div>
          )}

          {/* Variant pills */}
          {!selectMode && (img.emptyUrl || img.stagedUrl || img.brandedUrl || img.brandedEmptyUrl) && (
            <div className="absolute bottom-2 left-2 flex gap-1">
              {img.emptyUrl && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewOverride((prev) => ({ ...prev, [img.id]: 'empty' }));
                  }}
                  className={cn(
                    'px-2 py-0.5 rounded-full text-[11px] font-semibold backdrop-blur-sm transition-colors',
                    kind === 'empty'
                      ? 'bg-white text-slate-900'
                      : 'bg-black/40 text-white hover:bg-black/60'
                  )}
                >
                  Leer
                </button>
              )}
              {img.stagedUrl && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewOverride((prev) => ({ ...prev, [img.id]: 'staged' }));
                  }}
                  className={cn(
                    'px-2 py-0.5 rounded-full text-[11px] font-semibold backdrop-blur-sm transition-colors',
                    kind === 'staged'
                      ? 'bg-white text-slate-900'
                      : 'bg-black/40 text-white hover:bg-black/60'
                  )}
                >
                  Eingerichtet
                </button>
              )}
              {(img.brandedUrl || img.brandedEmptyUrl) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewOverride((prev) => ({ ...prev, [img.id]: 'branded' }));
                  }}
                  className={cn(
                    'px-2 py-0.5 rounded-full text-[11px] font-semibold backdrop-blur-sm transition-colors',
                    kind === 'branded'
                      ? 'bg-white text-slate-900'
                      : 'bg-black/40 text-white hover:bg-black/60'
                  )}
                >
                  Gebrandet
                </button>
              )}
            </div>
          )}

          {/* Hover-Aktionen: Leerräumen / Einrichten + Schnell-Download */}
          {!selectMode && (
            <div className="absolute top-2 right-2 flex flex-wrap justify-end gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  startFlowForImages([img.id], 2);
                }}
                className="h-8 pl-2 pr-2.5 gap-1 rounded-full bg-black/50 hover:bg-black/70 text-white text-xs font-semibold flex items-center shadow-sm backdrop-blur-sm"
                title="Raum leerräumen"
              >
                <Eraser className="w-3.5 h-3.5" />
                Leerräumen
              </button>
              {img.emptyUrl && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    startFlowForImages([img.id], 3);
                  }}
                  className="h-8 pl-2 pr-2.5 gap-1 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-semibold flex items-center shadow-sm"
                  title="Auf dem leergeräumten Bild einrichten"
                >
                  <Sofa className="w-3.5 h-3.5" />
                  Einrichten
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void handleQuickDownload(img, kind);
                }}
                className="w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center shrink-0"
                title="Schnell-Download"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
        <CardContent className="p-3">
          <p className="text-sm font-semibold text-foreground truncate">{name}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{statusLine(img)}</p>
        </CardContent>
      </Card>
    );
  };

  const filterButton = (value: GalleryFilter, label: string) => (
    <button
      onClick={() => {
        setFilter(value);
        setPreviewOverride({});
      }}
      className={cn(
        'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
        filter === value
          ? 'bg-background text-foreground shadow'
          : 'text-muted-foreground hover:text-foreground'
      )}
    >
      {label}
    </button>
  );

  const exportGroup = (group: 'leer' | 'ein' | 'plan', title: string) => {
    const tiles = exportTiles.filter((t) => t.group === group);
    if (tiles.length === 0) return null;
    return (
      <div key={group}>
        <h3 className="text-sm font-semibold text-foreground mt-6 mb-2">
          {title} <span className="text-muted-foreground font-normal">· {tiles.length}</span>
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {tiles.map((tile) => {
            const selected = !exportDeselected.has(tile.key);
            return (
              <button
                key={tile.key}
                onClick={() => toggleExportTile(tile.key)}
                className={cn(
                  'relative aspect-[4/3] rounded-xl overflow-hidden border-2 transition-all text-left',
                  selected
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-transparent opacity-50 hover:opacity-80'
                )}
              >
                <img src={tile.url} alt={tile.label} className="w-full h-full object-cover" />
                {group === 'plan' && (
                  <span className="absolute top-1.5 left-1.5 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                    {tile.branded ? 'eigenes Branding · kein Overlay' : 'kein Overlay'}
                  </span>
                )}
                <span className="absolute bottom-1.5 left-2 text-[11px] font-semibold text-white drop-shadow">
                  {tile.label}
                </span>
                {selected && (
                  <span className="absolute top-1.5 right-1.5 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const createdLabel = new Date(project.createdAt).toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const workspaceTabs: { id: WorkspaceTab; label: string }[] = [
    { id: 'bilder', label: `Staging (${galleryImages.length})` },
    { id: 'grundriss', label: 'Grundriss' },
    { id: 'export', label: 'Export' },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} title="Zurück zur Übersicht">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          {isRenaming ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void submitRename();
                  if (e.key === 'Escape') setIsRenaming(false);
                }}
                onBlur={() => void submitRename()}
                disabled={isSavingName}
                maxLength={120}
                className="w-full max-w-md rounded-lg border-2 border-primary bg-background px-2 py-1 text-2xl font-bold text-foreground focus:outline-none disabled:opacity-60"
              />
              {isSavingName && <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin text-muted-foreground" />}
            </div>
          ) : (
            <div className="group flex items-center gap-2">
              <h1 className="truncate text-2xl font-bold text-foreground">{project.name}</h1>
              <button
                type="button"
                onClick={startRename}
                title="Projekt umbenennen"
                className="flex-shrink-0 rounded-md p-1.5 text-muted-foreground opacity-0 transition-colors hover:bg-muted hover:text-foreground focus:opacity-100 group-hover:opacity-100"
              >
                <Pencil className="h-4 w-4" />
              </button>
            </div>
          )}
          <p className="text-sm text-muted-foreground mt-0.5">
            {galleryImages.length} {galleryImages.length === 1 ? 'Bild' : 'Bilder'} ·{' '}
            {hasFloorplan ? 'Grundriss vorhanden' : 'Kein Grundriss'} · erstellt {createdLabel}
          </p>
        </div>
      </div>

      {/* Angefangenen Assistenten-Durchlauf fortsetzen (Session in browser memory) */}
      {hasResumableFlow && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
          <PlayCircle className="h-5 w-5 flex-shrink-0 text-primary" />
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-semibold text-foreground">Angefangener Durchlauf</p>
            <p className="text-xs text-muted-foreground">
              Du hast den Assistenten für dieses Projekt nicht abgeschlossen — mach dort weiter, wo du aufgehört hast.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDiscardRun}
              className={cn('gap-1.5', discardArmed && 'text-red-600')}
            >
              {discardArmed ? 'Wirklich verwerfen?' : 'Verwerfen'}
            </Button>
            <Button size="sm" onClick={onAddImages} className="gap-1.5">
              <PlayCircle className="h-4 w-4" /> Fortsetzen
            </Button>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as WorkspaceTab)}>
        <TabsList className="h-auto gap-1 rounded-full bg-muted p-1">
          {workspaceTabs.map((t) => (
            <TabsTrigger
              key={t.id}
              value={t.id}
              className="relative rounded-full px-4 py-2 text-sm font-semibold bg-transparent text-muted-foreground shadow-none transition-colors hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
            >
              {tab === t.id && (
                <motion.span
                  layoutId="workspace-tab-pill"
                  className="absolute inset-0 rounded-full bg-primary shadow-sm"
                  transition={
                    prefersReducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 380, damping: 30 }
                  }
                />
              )}
              <span className="relative z-10">{t.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ════════ Tab: Bilder ════════ */}
        <TabsContent value="bilder" className="mt-4 space-y-4">
          {isLoadingImages ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
          ) : imagesError ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <AlertTriangle className="w-8 h-8 text-amber-500" />
              <p className="text-sm text-muted-foreground">{imagesError}</p>
              <Button variant="outline" onClick={() => void loadImages(true)}>
                Erneut versuchen
              </Button>
            </div>
          ) : galleryImages.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
                <Sofa className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">
                Noch keine Bilder in diesem Projekt
              </h2>
              <p className="text-sm text-muted-foreground max-w-md">
                Lade Fotos hoch und lass sie in einem Rutsch leeren und einrichten — dauert nur ein
                paar Minuten.
              </p>
              <Button onClick={onAddImages} className="gap-2 mt-2">
                <Plus className="w-4 h-4" />
                Bilder generieren
              </Button>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
                <Link2 className="w-3.5 h-3.5" />
                Oder sende einen Mieter-Link — die Fotos landen automatisch hier.
              </p>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={onAddImages} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Bilder hinzufügen
                </Button>
                <div className="flex-1" />
                <div className="inline-flex items-center rounded-lg bg-muted p-1">
                  {filterButton('alle', 'Alle')}
                  {filterButton('ein', 'Eingerichtet')}
                  {filterButton('leer', 'Möbel entfernt')}
                </div>
                {selectMode ? (
                  <Button variant="outline" onClick={exitSelectMode}>
                    Auswahl aufheben
                  </Button>
                ) : (
                  <Button variant="outline" onClick={() => setSelectMode(true)}>
                    Auswählen
                  </Button>
                )}
              </div>

              {/* Selection bar */}
              {selectMode && selectedIds.size > 0 && (
                <div>
                <div className="flex flex-wrap items-center gap-3 rounded-t-xl bg-slate-900 text-white px-4 py-3">
                  <span className="text-sm font-semibold">{selectedIds.size} ausgewählt</span>
                  <button
                    onClick={() => {
                      setSelectedIds(new Set());
                      setConfirmBulkDelete(false);
                    }}
                    className="text-sm text-slate-300 hover:text-white transition-colors"
                  >
                    Abwählen
                  </button>
                  <div className="flex-1" />
                  <Button
                    size="sm"
                    onClick={() => startFlowForImages(Array.from(selectedIds), 2)}
                    className="gap-1.5 bg-white/10 text-white hover:bg-white/20 border border-white/20"
                  >
                    <Eraser className="w-4 h-4" />
                    Leerräumen
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => startFlowForImages(selectedEmptyIds, 3)}
                    disabled={selectedEmptyIds.length === 0}
                    title={
                      selectedEmptyIds.length === 0
                        ? 'Nur für bereits leergeräumte Bilder verfügbar'
                        : undefined
                    }
                    className="gap-1.5 bg-white text-slate-900 hover:bg-slate-100 disabled:opacity-40"
                  >
                    <Sofa className="w-4 h-4" />
                    Einrichten{selectedEmptyIds.length > 0 ? ` (${selectedEmptyIds.length})` : ''}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => void handleBulkDelete()}
                    disabled={isBulkDeleting}
                    className="gap-1.5"
                  >
                    {isBulkDeleting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    {confirmBulkDelete ? 'Wirklich löschen?' : 'Löschen'}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void handleBulkDownload()}
                    disabled={isBulkZipping}
                    className="gap-1.5"
                  >
                    {isBulkZipping ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    Download (ZIP)
                  </Button>
                </div>
                {/* Letzte Export-Option — direkt an der Aktionsleiste, gilt für den ZIP-Download */}
                <div className="rounded-b-xl border border-t-0 border-border bg-card px-4 pb-3">
                  <AiLabelOption {...aiLabel} className="border-t-0 pt-3" />
                </div>
                </div>
              )}

              {/* Grid */}
              {filteredImages.length === 0 ? (
                <p className="text-sm text-muted-foreground py-10 text-center">
                  Keine Bilder in diesem Filter.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredImages.map((img, idx) => renderGalleryCard(img, idx))}
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* ════════ Tab: Grundriss ════════ */}
        <TabsContent value="grundriss" className="mt-4">
          {floorplanResume && (
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                <LayoutGrid className="h-5 w-5 text-primary" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">Grundrisserstellung fortsetzen</p>
                <p className="text-xs text-muted-foreground">
                  Zuletzt bei: {FLOORPLAN_STEP_LABEL[floorplanResume.view]}
                </p>
              </div>
              <Button size="sm" onClick={onOpenFloorplan} className="gap-1.5">
                <PlayCircle className="h-4 w-4" /> Fortsetzen
              </Button>
            </div>
          )}
          {isLoadingFloorplan ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
          ) : hasFloorplan ? (
            <div className="space-y-4">
              {/* Vorher / Nachher */}
              <Card>
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-lg font-semibold text-foreground">Vorher &amp; Nachher</h2>
                    <span className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground">
                      <Maximize2 className="w-3.5 h-3.5" />
                      Zum Vergrößern klicken
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {floorplanTiles.map((tile) => (
                      <div key={tile.key} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground">{tile.label}</span>
                            <Badge variant={tile.accent ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0">
                              {tile.sub}
                            </Badge>
                          </div>
                          <button
                            type="button"
                            onClick={() => void downloadFloorplanTile(tile.url, tile.name)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            title="Herunterladen"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFloorplanLightbox({ url: tile.url, name: tile.name })}
                          className={cn(
                            'group relative block w-full aspect-[4/3] rounded-xl overflow-hidden bg-muted border transition-shadow hover:shadow-md',
                            tile.accent ? 'border-primary/30' : 'border-border'
                          )}
                        >
                          <img
                            src={tile.url}
                            alt={`${tile.label} (${tile.sub})`}
                            className="w-full h-full object-contain"
                          />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                            <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </span>
                        </button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Details & Aktionen */}
              <Card>
                <CardContent className="p-5 flex flex-col md:flex-row md:items-start md:justify-between gap-5">
                  <div className="flex-1 space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <Badge variant={has2d ? 'default' : 'outline'} className="gap-1">
                        <LayoutGrid className="w-3 h-3" />
                        {has2d ? '2D vorhanden ✓' : '2D noch nicht erstellt'}
                      </Badge>
                      <Badge variant={has3d ? 'default' : 'outline'} className="gap-1">
                        <Box className="w-3 h-3" />
                        {has3d ? '3D vorhanden ✓' : '3D noch nicht erstellt'}
                      </Badge>
                    </div>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 text-sm">
                      {floorplanCreatedLabel && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                          <dt className="text-muted-foreground">Erstellt:</dt>
                          <dd className="text-foreground font-medium">{floorplanCreatedLabel}</dd>
                        </div>
                      )}
                      {floorplanTextLabel && (
                        <div className="flex items-center gap-2">
                          <Type className="w-4 h-4 text-muted-foreground shrink-0" />
                          <dt className="text-muted-foreground">Original-Beschriftung:</dt>
                          <dd className="text-foreground font-medium">{floorplanTextLabel}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                  <div className="flex flex-col gap-2 md:items-end md:text-right shrink-0">
                    <Button onClick={onOpenFloorplan} className="gap-2">
                      <LayoutGrid className="w-4 h-4" />
                      Grundriss-Tool öffnen
                    </Button>
                    {floorplan?.storage_path_floorplan_2d_branded || floorplan?.storage_path_floorplan_3d_branded ? (
                      <Button variant="outline" onClick={onOpenFloorplanBranding} className="gap-2">
                        <Palette className="w-4 h-4" />
                        Branding bearbeiten
                      </Button>
                    ) : (
                      <Button variant="secondary" onClick={onOpenFloorplanBranding} className="gap-2">
                        <Palette className="w-4 h-4" />
                        Grundriss branden
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
                <LayoutGrid className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Noch kein Grundriss</h2>
              <p className="text-sm text-muted-foreground max-w-md">
                Lade deinen flachen Grundriss hoch (PDF oder Bild) — wir machen daraus einen sauberen
                2D-Plan und ein 3D-Modell. Nachbessern ist kostenlos.
              </p>
              <Button onClick={onOpenFloorplan} className="gap-2 mt-2">
                <LayoutGrid className="w-4 h-4" />
                Grundriss-Tool öffnen
              </Button>
            </div>
          )}
        </TabsContent>

        {/* ════════ Tab: Export ════════ */}
        <TabsContent value="export" className="mt-4 space-y-4">
          {exportTiles.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
                <Download className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Noch nichts zu exportieren</h2>
              <p className="text-sm text-muted-foreground max-w-md">
                Sobald Bilder generiert sind, kannst du sie hier mit oder ohne Overlay als ZIP
                herunterladen.
              </p>
              <Button onClick={onAddImages} className="gap-2 mt-2">
                <Plus className="w-4 h-4" />
                Bilder generieren
              </Button>
            </div>
          ) : (
            <>
              <input
                ref={overlayFileInputRef}
                type="file"
                accept="image/png"
                className="hidden"
                onChange={(e) => void handleOverlayFileSelected(e)}
              />
              {/* Download */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1" />
                <Button
                  onClick={() => void handleExportDownload()}
                  disabled={isExporting || selectedExportTiles.length === 0}
                  className="gap-2"
                >
                  {isExporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Herunterladen ({selectedExportTiles.length})
                </Button>
              </div>

              {exportError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {exportError}
                </p>
              )}

              {/* Overlay-Bereich: Auswahl + Verwaltung (links) neben Live-Vorschau (rechts) */}
              <div className={cn('grid grid-cols-1 gap-4 items-start', showExportPreview && 'lg:grid-cols-2')}>
                {/* Links: Overlay-Wahl + Mehrfach-Auswahl + Verwalten-Link */}
                <div className="space-y-3">
                  {/* Exportqualität — gestapelt statt übersehbarem Toggle */}
                  <div className="space-y-2">
                    <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Exportqualität
                    </span>
                    {(
                      [
                        { value: 'full' as ExportQuality, label: 'Volle Qualität', hint: 'Maximale Auflösung (PNG)' },
                        { value: 'web' as ExportQuality, label: 'Web-optimiert', hint: 'Komprimiert für Portale & Web (JPG)' },
                      ]
                    ).map(({ value, label, hint }) => (
                      <button
                        key={value}
                        onClick={() => setExportQuality(value)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-xl border-2 p-3 text-left transition-all',
                          exportQuality === value
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-muted-foreground/40'
                        )}
                      >
                        <div
                          className={cn(
                            'w-4 h-4 rounded-full border-2 flex-shrink-0',
                            exportQuality === value ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                          )}
                        />
                        <div>
                          <p className="text-sm font-semibold text-foreground">{label}</p>
                          <p className="text-xs text-muted-foreground">{hint}</p>
                        </div>
                      </button>
                    ))}
                  </div>

                  <span className="block pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Overlay
                  </span>

                  <div className="space-y-2">
                    <button
                      onClick={() => setOverlayChoice('none')}
                      className={cn(
                        'flex items-center gap-3 rounded-xl border-2 p-3 text-left transition-all w-full',
                        overlayChoice === 'none'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/40'
                      )}
                    >
                      <div
                        className={cn(
                          'w-4 h-4 rounded-full border-2 flex-shrink-0',
                          overlayChoice === 'none' ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                        )}
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground">Ohne Overlay</p>
                        <p className="text-xs text-muted-foreground">Bilder unverändert exportieren</p>
                      </div>
                    </button>

                    <button
                      onClick={handleSelectOwnOverlay}
                      disabled={isLoadingOverlays}
                      className={cn(
                        'flex items-center gap-3 rounded-xl border-2 p-3 text-left transition-all disabled:opacity-50 w-full',
                        overlayChoice === 'own' && activeOverlay
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/40'
                      )}
                    >
                      <div
                        className={cn(
                          'w-4 h-4 rounded-full border-2 flex-shrink-0',
                          overlayChoice === 'own' && activeOverlay
                            ? 'border-primary bg-primary'
                            : 'border-muted-foreground/40'
                        )}
                      />
                      {activeOverlay ? (
                        <>
                          <div className="w-16 h-12 rounded-md overflow-hidden bg-muted border flex-shrink-0">
                            <img
                              src={activeOverlay.publicUrl}
                              alt={activeOverlay.name}
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-foreground">Mit eigenem Overlay</p>
                            <p className="text-xs text-muted-foreground truncate">
                              „{activeOverlay.name}“ · zuletzt hochgeladen
                            </p>
                          </div>
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              openOverlayUpload();
                            }}
                            className="p-2 rounded-full hover:bg-muted transition-colors"
                            title="Neues Overlay hochladen"
                          >
                            {isUploadingOverlay ? (
                              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                            ) : (
                              <Upload className="w-4 h-4 text-muted-foreground" />
                            )}
                          </span>
                        </>
                      ) : (
                        <div>
                          <p className="text-sm font-semibold text-foreground">Overlay hochladen</p>
                          <p className="text-xs text-muted-foreground">
                            Noch keins gespeichert — PNG mit Transparenz hochladen
                          </p>
                        </div>
                      )}
                    </button>
                  </div>

                  {/* Overlay-Auswahl (mehrere) + Upload-Link (immer erreichbar) */}
                  {displayOverlays.length > 0 && (
                    <div className="space-y-2">
                      {displayOverlays.length > 1 && overlayChoice === 'own' && (
                        <div className="space-y-1.5">
                          <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Overlay wählen
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {displayOverlays.map((asset) => {
                              const active = asset.id === activeOverlay?.id;
                              return (
                                <button
                                  key={asset.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedOverlayId(asset.id);
                                    setOverlayChoice('own');
                                  }}
                                  title={asset.name}
                                  className={cn(
                                    'flex w-32 flex-col overflow-hidden rounded-lg border-2 text-left transition-all',
                                    active ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/40'
                                  )}
                                >
                                  <span className="relative block h-16 w-full bg-muted">
                                    <img src={asset.publicUrl} alt={asset.name} className="h-full w-full object-contain" />
                                    {active && (
                                      <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
                                        <Check className="h-2.5 w-2.5" />
                                      </span>
                                    )}
                                  </span>
                                  <span className="truncate px-2 py-1 text-[11px] font-medium text-foreground">{asset.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={openOverlayUpload}
                        disabled={isUploadingOverlay}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline disabled:opacity-50"
                      >
                        {isUploadingOverlay ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                        Neues Overlay hochladen
                      </button>
                    </div>
                  )}

                  {/* Wasserzeichen (Parität zum Wizard-Schritt 4) */}
                  {watermarkAssets.length > 0 && (
                    <>
                      <span className="block pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Wasserzeichen
                      </span>
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          {watermarkAssets.map((asset) => {
                            const active = asset.id === selectedWatermarkId;
                            return (
                              <button
                                key={asset.id}
                                type="button"
                                onClick={() => setSelectedWatermarkId(active ? null : asset.id)}
                                title={active ? `${asset.name} — Klick entfernt die Auswahl` : asset.name}
                                className={cn(
                                  'relative h-16 w-24 overflow-hidden rounded-lg border-2 bg-muted/30 transition-all',
                                  active ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/40'
                                )}
                              >
                                <img src={asset.publicUrl} alt={asset.name} className="h-full w-full object-contain p-1" />
                                {active && (
                                  <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
                                    <Check className="h-2.5 w-2.5" />
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {selectedWatermark && (
                          <div className="space-y-3 rounded-xl border border-border p-3">
                            <div>
                              <div className="mb-1 flex items-center justify-between">
                                <span className="text-xs font-medium text-foreground">Transparenz</span>
                                <span className="font-mono text-xs">{watermarkOpacity}%</span>
                              </div>
                              <Slider min={5} max={80} step={5} value={[watermarkOpacity]} onValueChange={([v]) => setWatermarkOpacity(v)} />
                            </div>
                            <div>
                              <div className="mb-1 flex items-center justify-between">
                                <span className="text-xs font-medium text-foreground">Größe</span>
                                <span className="font-mono text-xs">{Math.round(watermarkScale * 100)}%</span>
                              </div>
                              <Slider min={10} max={100} step={5} value={[watermarkScale * 100]} onValueChange={([v]) => setWatermarkScale(v / 100)} />
                            </div>
                            <div>
                              <span className="mb-1 block text-xs font-medium text-foreground">Position</span>
                              <div className="flex flex-wrap gap-1.5">
                                {WATERMARK_POSITIONS.map(({ position, label }) => (
                                  <button
                                    key={position}
                                    type="button"
                                    onClick={() => setWatermarkPosition(position)}
                                    className={cn(
                                      'rounded-md border px-2 py-1 text-[11px] font-medium transition-colors',
                                      watermarkPosition === position
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-border text-muted-foreground hover:border-primary/40'
                                    )}
                                  >
                                    {label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Logo */}
                  {logoAssets.length > 0 && (
                    <>
                      <span className="block pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Logo
                      </span>
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          {logoAssets.map((asset) => {
                            const active = asset.id === selectedLogoId;
                            return (
                              <button
                                key={asset.id}
                                type="button"
                                onClick={() => setSelectedLogoId(active ? null : asset.id)}
                                title={active ? `${asset.name} — Klick entfernt die Auswahl` : asset.name}
                                className={cn(
                                  'relative h-16 w-24 overflow-hidden rounded-lg border-2 bg-muted/30 transition-all',
                                  active ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/40'
                                )}
                              >
                                <img src={asset.publicUrl} alt={asset.name} className="h-full w-full object-contain p-1" />
                                {active && (
                                  <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
                                    <Check className="h-2.5 w-2.5" />
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {selectedLogo && (
                          <div className="flex flex-wrap gap-1.5">
                            {LOGO_POSITIONS.map(({ position, label }) => (
                              <button
                                key={position}
                                type="button"
                                onClick={() => setLogoPosition(position)}
                                className={cn(
                                  'rounded-md border px-2 py-1 text-[11px] font-medium transition-colors',
                                  logoPosition === position
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-border text-muted-foreground hover:border-primary/40'
                                )}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Letzte Export-Option — nach allen Branding-Ebenen */}
                  <AiLabelOption {...aiLabel} />
                </div>

                {/* Rechts: Live-Vorschau */}
                <div>
                  {showExportPreview && (
                    <div className="rounded-xl border border-border p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                        So sieht dein Export aus
                      </p>
                      <div className="relative mx-auto w-full max-w-md aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                        {isPreviewing && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
                          </div>
                        )}
                        <img
                          src={overlayPreview ?? previewSample}
                          alt="Overlay-Vorschau"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tile groups */}
              {exportGroup('leer', 'Möbel entfernt')}
              {exportGroup('ein', 'Eingerichtet')}
              {exportGroup('plan', 'Grundriss')}

            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Bild-Popup */}
      {inspectorIndex !== null && inspectorRooms.length > 0 && (
        <ImageInspectorModal
          open
          rooms={inspectorRooms}
          index={Math.min(inspectorIndex, inspectorRooms.length - 1)}
          projectId={project.id}
          stagingStyle={project.stagingStyle ?? 'modern'}
          aspectRatio={project.aspectRatio ?? '4:3'}
          isAdmin={isAdmin}
          context="workspace"
          onIndexChange={setInspectorIndex}
          onClose={() => setInspectorIndex(null)}
          onChanged={() => {
            void loadImages(false);
            void loadFloorplan();
          }}
          onCreditsRefresh={onCreditsRefresh}
        />
      )}

      {/* Grundriss-Lightbox */}
      {floorplanLightbox && (
        <Lightbox
          url={floorplanLightbox.url}
          onClose={() => setFloorplanLightbox(null)}
          onDownload={() => void downloadFloorplanTile(floorplanLightbox.url, floorplanLightbox.name)}
        />
      )}
    </div>
  );
};
